import { FC, useEffect, useState } from 'react';
import { HEADING_VARIANTS, IPlanCard } from '@belong/types';
import { AccentStrip, ACCENTSTRIP_VARIANTS } from '@belong/ui-core';
import { FLAIRS } from '@belong/themes';
import { SERVICE_URLS } from '@belong/url-utils';
import { useAnalyticsEvents } from '@belong/providers';
import { buttonClick, ANALYTICS_STRINGS, getAnalyticsPlanSpeed } from '@belong/analytics';
import { PlanDifferentiator } from './PlanDifferentiator/PlanDifferentiator';
import { Heading } from '../../styles/Typography';
import * as styles from './PlanCard.styles';
import { getStatusDetails, isMobilePlan, isNbnPlan } from './utils';
import { IMultipleAccordionPlanCardProps } from './types';
import { PLAN_CARD_TEST_ID } from './testIds';
import { PlanCardBodyNBN } from './PlanCardBody.nbn';
import { PlanCardBodyMobile } from './PlanCardBody.mobile';
import CONTENT from './content.json';

/**
 * Plan Cards are the components we use to show our product plans to our customers. The Plan Card is used to
 * entice the customer to either select, activate or change a plan.
 */
export const PlanCard: FC<IPlanCard & IMultipleAccordionPlanCardProps> = props => {
  const {
    selected,
    planType,
    hasTopPadding = false,
    differentiatorLabel: defaultDifferentiatorLabel,
    differentiatorIcon: defaultDifferentiatorIcon,
    cardStripColour: defaultCardStripColour = FLAIRS.BLUE,
    title,
    plan,
    activityLevel,
    buttonLabel,
    onPrimaryButtonClick,
    current,
    isWithinCarousel = false,
    isPreSq
  } = props;

  const analyticsEvents = useAnalyticsEvents();
  const [buttonUrl, setButtonUrl] = useState('');
  const [overrideButtonLabel, setOverrideButtonLabel] = useState<string>();
  const planDifferentiatorId = `plan-differentiator-${plan.productId}${isWithinCarousel ? '-carousel' : ''}`;

  // If permitted is not in the plan details then we assume it is permitted
  const isPlanPermitted = plan.permitted ?? true;

  useEffect(() => {
    // Set CTA URL based on plan type
    if (isMobilePlan(planType)) {
      setButtonUrl(SERVICE_URLS.ORDER_SIM);
    } else if (isPreSq) {
      // current url without query string and #
      const currentPath = window.location.pathname;
      setButtonUrl(`${currentPath || ''}#address-check`);

      // Depending on the campaign we may want to render a different label
      const campaignLabel: string | undefined = plan.campaignCode && CONTENT.campaigns[plan.campaignCode];
      setOverrideButtonLabel(campaignLabel ?? CONTENT.campaigns.default);
    }
  }, [planType, plan.campaignCode, isPreSq]);

  // Set selection status and strip colour
  const {
    cardStripColour = defaultCardStripColour,
    differentiatorIcon = defaultDifferentiatorIcon,
    differentiatorLabel = defaultDifferentiatorLabel,
    selectionStatus
  } = getStatusDetails({ current, isPlanPermitted, selected });

  const onPlanCardCtaClick = (evt: React.MouseEvent): void => {
    if (isMobilePlan(planType)) {
      analyticsEvents.send({
        ...buttonClick({ label: 'plan-card-primary-button', text: buttonLabel }),
        component: ANALYTICS_STRINGS.COMPONENT.PLAN_CARD,
        /*
         * The Plan product you're clicking is potentially just one of many products you might add to your cart
         * so this is sent as a list.
         */
        products: [
          {
            productCode: plan.productCode,
            productName: plan.productName,
            productId: plan.productId,
            description: plan.description,
            currency: 'AUD',
            serviceType: 'new',
            category: (plan as any).productType,
            planSpeed: getAnalyticsPlanSpeed(plan),
            quantity: 1,
            price: plan.price.value
          }
        ]
      });
    }

    if (onPrimaryButtonClick) {
      onPrimaryButtonClick(evt);
    }
  };

  const wrapperProps = {
    'data-testid': PLAN_CARD_TEST_ID.root,
    'data-productcode': plan.productCode
  };

  // TODO: remove the check for activityLevel when the content is deployed
  const useInternetPlanLayout = isNbnPlan(planType) && !!activityLevel;

  const additionalProps = {
    buttonUrl,
    onPlanCardCtaClick,
    selectionStatus
  };

  const combinedProps = { ...props, ...additionalProps };

  return (
    <styles.Outerwrapper withTopPadding={hasTopPadding && !differentiatorLabel} {...wrapperProps}>
      {differentiatorLabel && (
        <PlanDifferentiator
          id={planDifferentiatorId}
          icon={differentiatorIcon}
          background={cardStripColour}
          content={differentiatorLabel}
        />
      )}
      <styles.Wrapper withDifferentiator={!!differentiatorLabel}>
        <AccentStrip variant={ACCENTSTRIP_VARIANTS[cardStripColour]} hasOpacity={false} isSolid />
        <styles.FlexChildGrow>
          <styles.FullHeightStack space="xxsmall">
            <styles.PlanTitle isPlanSelected={selected}>
              <Heading
                alignment="center"
                as="h3"
                variant={HEADING_VARIANTS.small}
                aria-label={title}
                aria-describedby={planDifferentiatorId}
              >
                {title}
              </Heading>
            </styles.PlanTitle>

            <styles.PlanBody>
              {/* render the nbn card with the activityLevel or the mobile card with the disclaimer */}
              {useInternetPlanLayout ? (
                <PlanCardBodyNBN {...combinedProps} overrideButtonLabel={overrideButtonLabel} />
              ) : (
                <PlanCardBodyMobile {...combinedProps} overrideButtonLabel={overrideButtonLabel} />
              )}
            </styles.PlanBody>
          </styles.FullHeightStack>
        </styles.FlexChildGrow>
      </styles.Wrapper>
    </styles.Outerwrapper>
  );
};

PlanCard.displayName = 'PlanCard';

export default PlanCard;
